import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Card,
  Grid,
  Button,
} from '@mui/material';
import {
  Storage,
  SearchOff,
  ErrorOutline,
  Refresh,
} from '@mui/icons-material';
import {
  useGetReposQuery,
  useGetScanQuery,
  useTriggerFixMutation,
  type Issue,
} from '../../store/api/githubApi';
import { RepositorySelector } from './RepositorySelector';
import { RepositorySummary } from './RepositorySummary';
import { ImprovementObjective } from './ImprovementObjective';
import { RecommendedFixes } from './RecommendedFixes';
import { ImpactPreview } from './ImpactPreview';
import { ImpactSummary } from './ImpactSummary';
import { RemediationPreviewDrawer } from './RemediationPreviewDrawer';
import {
  sortIssuesByObjective,
  computeImpactSimulation,
  type ImprovementObjectiveType,
} from './simulatorUtils';
import { IssueDetailModal } from '../scans/components/IssueDetailModal';
import RemediationProgressModal from '../scans/components/RemediationProgressModal';

export const ImpactSimulatorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 1. Repositories
  const { data: repos = [], isLoading: isLoadingRepos } = useGetReposQuery();

  // 2. Selected Repository ID
  const repoIdParam = searchParams.get('repoId');
  const [selectedRepoId, setSelectedRepoId] = useState<number | null>(null);

  // Active repository tracking ref to completely avoid race conditions during fast switching
  const activeRepoIdRef = useRef<number | null>(null);

  // Initialize selected repo: param > first scanned repo > first repo
  useEffect(() => {
    if (repos.length > 0 && selectedRepoId === null) {
      if (repoIdParam) {
        const found = repos.find((r) => r.id === Number(repoIdParam));
        if (found) {
          setSelectedRepoId(found.id);
          activeRepoIdRef.current = found.id;
          return;
        }
      }
      const withScan = repos.find((r) => r.latest_scan_id !== null);
      if (withScan) {
        setSelectedRepoId(withScan.id);
        activeRepoIdRef.current = withScan.id;
      } else {
        setSelectedRepoId(repos[0].id);
        activeRepoIdRef.current = repos[0].id;
      }
    }
  }, [repos, repoIdParam, selectedRepoId]);

  const selectedRepo = useMemo(
    () => repos.find((r) => r.id === selectedRepoId) ?? null,
    [repos, selectedRepoId]
  );

  // 3. Scan & Findings Data
  const currentScanId = selectedRepo?.latest_scan_id ?? 0;
  const {
    data: rawScan,
    isLoading: isLoadingScanQuery,
    isFetching: isFetchingScanQuery,
    isError: isScanError,
    refetch: refetchScan,
  } = useGetScanQuery(currentScanId, {
    skip: !selectedRepo?.latest_scan_id,
  });

  // Strict validation: only treat scan as valid if it actually matches the currently selected repo's latest_scan_id
  const isCurrentScanValid = Boolean(
    rawScan &&
    selectedRepo?.latest_scan_id &&
    rawScan.id === selectedRepo.latest_scan_id &&
    activeRepoIdRef.current === selectedRepo.id
  );

  const scan = isCurrentScanValid ? rawScan : null;

  // The page is loading if repositories are loading, or if the current repo has a scan that is still in-flight
  const isCurrentScanLoading = Boolean(
    isLoadingRepos ||
    (selectedRepo?.latest_scan_id &&
      (!isCurrentScanValid || isLoadingScanQuery || isFetchingScanQuery))
  );

  // 4. Objective state
  const [objective, setObjective] = useState<ImprovementObjectiveType>('overall');

  // 5. Selected issues for simulation
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<number>>(new Set());

  // 6. Remediation preview & progress modal states
  const [isRemediationPreviewOpen, setIsRemediationPreviewOpen] = useState<boolean>(false);
  const [remediationModalOpen, setRemediationModalOpen] = useState<boolean>(false);
  const [remediationComplete, setRemediationComplete] = useState<boolean>(false);
  const [remediationPrUrl, setRemediationPrUrl] = useState<string | null>(null);
  const [remediationPrNumber, setRemediationPrNumber] = useState<number | null>(null);

  // 7. Modal state for viewing single issue details
  const [viewingIssue, setViewingIssue] = useState<Issue | null>(null);
  const [triggerFix, { isLoading: isFixing }] = useTriggerFixMutation();

  // Filter and sort issues according to selected objective
  const prioritizedIssues = useMemo(() => {
    if (!scan?.issues) return [];
    return sortIssuesByObjective(scan.issues, objective);
  }, [scan?.issues, objective]);

  // Initial load & State Preservation from sessionStorage when current repo's scan is valid
  const initializedScanRef = useRef<number | null>(null);

  useEffect(() => {
    if (!selectedRepoId || !isCurrentScanValid || !scan?.issues) return;

    // Avoid duplicate initialization for the exact same scan
    if (initializedScanRef.current === scan.id) return;
    initializedScanRef.current = scan.id;

    try {
      const savedSelected = sessionStorage.getItem(`kodegauge_sim_selected_${selectedRepoId}`);
      if (savedSelected) {
        const parsedIds: number[] = JSON.parse(savedSelected);
        const validIds = parsedIds.filter((id) => scan.issues.some((i) => i.id === id));
        if (validIds.length > 0) {
          setSelectedIssueIds(new Set(validIds));
          return;
        }
      }
    } catch {
      // Ignore sessionStorage parsing errors
    }

    // Default initialization: pre-select top 3 findings for immediate instant feedback
    if (prioritizedIssues.length > 0) {
      const top3Ids = prioritizedIssues.slice(0, 3).map((i) => i.id);
      setSelectedIssueIds(new Set(top3Ids));
      try {
        sessionStorage.setItem(
          `kodegauge_sim_selected_${selectedRepoId}`,
          JSON.stringify(top3Ids)
        );
      } catch {
        // Ignore
      }
    } else {
      setSelectedIssueIds(new Set());
    }
  }, [selectedRepoId, isCurrentScanValid, scan?.id, scan?.issues, prioritizedIssues]);

  // Handle repository selection change: immediately resets previous repository state and sets loading
  const handleSelectRepo = (repoId: number) => {
    if (repoId === selectedRepoId) return;

    // Immediately mark active repo to prevent race conditions from prior queries
    activeRepoIdRef.current = repoId;
    initializedScanRef.current = null;

    // Immediately clear selected findings from previous repository
    setSelectedIssueIds(new Set());
    setSelectedRepoId(repoId);
    setSearchParams({ repoId: String(repoId) });
  };

  // Handle objective change
  const handleChangeObjective = (newObjective: ImprovementObjectiveType) => {
    setObjective(newObjective);
    const reordered = sortIssuesByObjective(scan?.issues ?? [], newObjective);
    if (reordered.length > 0) {
      const top3Ids = reordered.slice(0, 3).map((i) => i.id);
      setSelectedIssueIds(new Set(top3Ids));
      if (selectedRepoId) {
        try {
          sessionStorage.setItem(
            `kodegauge_sim_selected_${selectedRepoId}`,
            JSON.stringify(top3Ids)
          );
        } catch {
          // Ignore
        }
      }
    }
  };

  // Toggle individual issue selection (updates real-time)
  const handleToggleIssue = (issueId: number) => {
    setSelectedIssueIds((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) {
        next.delete(issueId);
      } else {
        next.add(issueId);
      }
      if (selectedRepoId) {
        try {
          sessionStorage.setItem(
            `kodegauge_sim_selected_${selectedRepoId}`,
            JSON.stringify(Array.from(next))
          );
        } catch {
          // Ignore
        }
      }
      return next;
    });
  };

  const handleSelectTop = (count: number) => {
    const ids = prioritizedIssues.slice(0, count).map((i) => i.id);
    setSelectedIssueIds(new Set(ids));
    if (selectedRepoId) {
      try {
        sessionStorage.setItem(
          `kodegauge_sim_selected_${selectedRepoId}`,
          JSON.stringify(ids)
        );
      } catch {
        // Ignore
      }
    }
  };

  const handleSelectAll = () => {
    const ids = prioritizedIssues.slice(0, 15).map((i) => i.id);
    setSelectedIssueIds(new Set(ids));
    if (selectedRepoId) {
      try {
        sessionStorage.setItem(
          `kodegauge_sim_selected_${selectedRepoId}`,
          JSON.stringify(ids)
        );
      } catch {
        // Ignore
      }
    }
  };

  const handleClearSelection = () => {
    setSelectedIssueIds(new Set());
    if (selectedRepoId) {
      try {
        sessionStorage.removeItem(`kodegauge_sim_selected_${selectedRepoId}`);
      } catch {
        // Ignore
      }
    }
  };

  // Current selected findings list (derived in real-time)
  const selectedIssuesList = useMemo(() => {
    if (!scan?.issues) return [];
    return scan.issues.filter((i) => selectedIssueIds.has(i.id));
  }, [scan?.issues, selectedIssueIds]);

  // Real-time Impact calculation - updates instantly on every select/deselect
  const impact = useMemo(() => {
    if (!selectedRepo || !isCurrentScanValid) return null;
    return computeImpactSimulation(selectedRepo, scan, selectedIssuesList);
  }, [selectedRepo, scan, isCurrentScanValid, selectedIssuesList]);

  // Preview Remediation: Opens RemediationPreviewDrawer carrying over current selected findings
  const handlePreviewRemediation = () => {
    if (selectedIssuesList.length > 0 && scan?.id) {
      setIsRemediationPreviewOpen(true);
    }
  };

  // Start automated PR remediation from drawer
  const handleStartRemediation = async () => {
    if (!scan?.id || selectedIssuesList.length === 0) return;
    setIsRemediationPreviewOpen(false);
    setRemediationComplete(false);
    setRemediationPrUrl(scan.fix_pr_url ?? null);
    setRemediationPrNumber(scan.fix_pr_number ?? null);
    setRemediationModalOpen(true);

    try {
      const issueIds = selectedIssuesList.map((i) => i.id);
      await triggerFix({ scan_id: scan.id, issue_ids: issueIds }).unwrap();
      refetchScan();
      setRemediationComplete(true);
      setRemediationPrUrl(scan.fix_pr_url ?? null);
      setRemediationPrNumber(scan.fix_pr_number ?? null);
    } catch (err: any) {
      console.error('Failed to trigger remediation', err);
      setRemediationComplete(true);
    }
  };

  // Actions for viewing issue in SideDrawer
  const handleViewIssue = (issue: Issue) => {
    setViewingIssue(issue);
  };

  const handleFixSingleIssue = async (issueId: number) => {
    if (!scan?.id) return;
    try {
      await triggerFix({ scan_id: scan.id, issue_ids: [issueId] }).unwrap();
      setViewingIssue(null);
      refetchScan();
      navigate(`/scans/${scan.id}/fixes`);
    } catch (err) {
      console.error('Failed to trigger fix', err);
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 2.5, sm: 3.5 },
        px: { xs: 2, sm: 3 },
      }}
    >
      {/* 1. Header & Repository Selector */}
      <RepositorySelector
        repositories={repos}
        selectedRepoId={selectedRepoId}
        onSelectRepo={handleSelectRepo}
        isLoading={isLoadingRepos}
      />

      {/* 2. Repository Summary & Content */}
      {selectedRepo ? (
        <>
          {/* Current Repository Summary - displays clean skeletons while current repo data loads */}
          <RepositorySummary
            repository={selectedRepo}
            scan={scan}
            isLoading={isCurrentScanLoading}
          />

          {/* Error State: Failed to load findings for this repo */}
          {isScanError && !isCurrentScanLoading ? (
            <Card
              elevation={0}
              sx={{
                p: { xs: 4, sm: 6 },
                textAlign: 'center',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                my: 4,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <ErrorOutline sx={{ fontSize: 28, color: '#dc2626' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                Unable to load repository data
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#64748b', maxWidth: 440, mx: 'auto', lineHeight: 1.5, mb: 3 }}
              >
                We couldn't load findings for {selectedRepo.name}.
              </Typography>
              <Button
                variant="contained"
                onClick={() => refetchScan()}
                startIcon={<Refresh />}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: '#2563eb',
                  color: '#ffffff',
                  px: 3,
                  py: 1,
                  '&:hover': { bgcolor: '#1d4ed8' },
                }}
              >
                Retry
              </Button>
            </Card>
          ) : isCurrentScanLoading ? (
            /* Loading State: Repository findings are in flight */
            <Box>
              <ImprovementObjective
                selectedObjective={objective}
                onChangeObjective={handleChangeObjective}
              />

              <Grid container spacing={3.5} alignItems="flex-start">
                {/* Left Column: Loading Findings Card */}
                <Grid item xs={12} md={7} lg={7.5}>
                  <Card
                    elevation={0}
                    sx={{
                      p: { xs: 5, sm: 7 },
                      textAlign: 'center',
                      borderRadius: '16px',
                      border: '1px solid #e2e8f0',
                      bgcolor: '#ffffff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: '#2563eb', mb: 2 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#0f172a',
                        mb: 0.5,
                        fontSize: '1.05rem',
                      }}
                    >
                      Loading repository data...
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                      Fetching findings for {selectedRepo.name}...
                    </Typography>
                  </Card>
                </Grid>

                {/* Right Column: Loading Impact Panel */}
                <Grid
                  item
                  xs={12}
                  md={5}
                  lg={4.5}
                  id="impact-preview-section"
                  sx={{
                    position: { md: 'sticky' },
                    top: { md: 84 },
                    zIndex: 10,
                    alignSelf: 'flex-start',
                    maxHeight: { md: 'calc(100vh - 100px)' },
                    overflowY: { md: 'auto' },
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <ImpactPreview impact={null} isLoading={true} />
                    <ImpactSummary
                      impact={null}
                      isLoading={true}
                      onPreviewRemediation={handlePreviewRemediation}
                      canRemediate={false}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : !selectedRepo.latest_scan_id || !scan || prioritizedIssues.length === 0 ? (
            /* Empty State: No findings available */
            <Card
              elevation={0}
              sx={{
                p: { xs: 4, sm: 6 },
                textAlign: 'center',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                my: 4,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <SearchOff sx={{ fontSize: 28, color: '#64748b' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                No issues available
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#64748b', maxWidth: 440, mx: 'auto', lineHeight: 1.5, mb: 3 }}
              >
                This repository currently has no findings available for remediation planning. Run a scan to
                populate codebase health and issue details.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate('/repositories')}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#2563eb',
                  borderColor: '#cbd5e1',
                  '&:hover': { borderColor: '#2563eb', bgcolor: 'rgba(37, 99, 235, 0.04)' },
                }}
              >
                View Repositories
              </Button>
            </Card>
          ) : (
            /* Main Simulation Workspace */
            <Box>
              {/* Improvement Objective Selector */}
              <ImprovementObjective
                selectedObjective={objective}
                onChangeObjective={handleChangeObjective}
              />

              {/* Two Column Layout (Left: Recommended Fixes, Right: Sticky Real-Time Impact Panel) */}
              <Grid container spacing={3.5} alignItems="flex-start">
                {/* Left Column: Recommended Fixes List */}
                <Grid item xs={12} md={7} lg={7.5}>
                  <RecommendedFixes
                    issues={prioritizedIssues}
                    selectedIssueIds={selectedIssueIds}
                    onToggleIssue={handleToggleIssue}
                    onSelectTop={handleSelectTop}
                    onSelectAll={handleSelectAll}
                    onClearSelection={handleClearSelection}
                    onViewIssue={handleViewIssue}
                  />
                </Grid>

                {/* Right Column: Sticky plan panel (impact preview + plan summary + preview remediation) */}
                <Grid
                  item
                  xs={12}
                  md={5}
                  lg={4.5}
                  id="impact-preview-section"
                  sx={{
                    position: { md: 'sticky' },
                    top: { md: 84 },
                    zIndex: 10,
                    alignSelf: 'flex-start',
                    maxHeight: { md: 'calc(100vh - 100px)' },
                    overflowY: { md: 'auto' },
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {impact && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      <ImpactPreview impact={impact} isLoading={false} />
                      <ImpactSummary
                        impact={impact}
                        isLoading={false}
                        onPreviewRemediation={handlePreviewRemediation}
                        canRemediate={Boolean(scan?.id)}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </>
      ) : (
        /* Empty State: No repository selected */
        <Card
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            textAlign: 'center',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            my: 4,
          }}
        >
          <Storage sx={{ fontSize: 44, color: '#94a3b8', mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
            Select a repository
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 440, mx: 'auto', lineHeight: 1.5 }}>
            Choose a repository above to see recommended improvements and prepare a remediation plan.
          </Typography>
        </Card>
      )}

      {/* Existing Issue Detail Modal Drawer */}
      <IssueDetailModal
        open={Boolean(viewingIssue)}
        onClose={() => setViewingIssue(null)}
        issue={viewingIssue}
        onFix={handleFixSingleIssue}
        isFixing={isFixing}
        scanId={scan?.id ? String(scan.id) : undefined}
      />

      {/* Remediation Preview Drawer carrying over selected fixes */}
      <RemediationPreviewDrawer
        open={isRemediationPreviewOpen}
        onClose={() => setIsRemediationPreviewOpen(false)}
        selectedIssues={selectedIssuesList}
        onRemediate={handleStartRemediation}
        isFixing={isFixing}
      />

      {/* Existing Remediation Progress Modal during PR generation */}
      <RemediationProgressModal
        open={remediationModalOpen}
        onClose={() => setRemediationModalOpen(false)}
        count={selectedIssuesList.length}
        isFixing={isFixing}
        isComplete={remediationComplete}
        prUrl={remediationPrUrl}
        prNumber={remediationPrNumber}
        onReviewChanges={() => {
          setRemediationModalOpen(false);
          navigate(`/scans/${scan?.id}/fixes`);
        }}
      />
    </Container>
  );
};

export default ImpactSimulatorPage;
